import Box from "@material-ui/core/esm/Box";
import TextField from "@material-ui/core/esm/TextField";
import WarningRounded from "@material-ui/icons/esm/WarningRounded";
import { nanoid } from 'nanoid';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormElement, InputAdornment } from 'components/Form';
import FormCountrySelector from 'components/FormCountrySelector/FormCountrySelector';
import InfoBox from 'components/InfoBox/InfoBox';
import { DEFAULT_LANGUAGE } from 'constants/language';
import { postalCodeRegex } from 'constants/regex';
import useGetValidationString from 'hooks/language/useGetValidationString';
import usePostalCodes from 'utils/postalCodes/postalCodes';
import useStyles from './AddressFormSectionStyles';
import { getUserDataFromSession } from 'utils/account/getUserDataFromSession';
import getRegistryAddressPrefill from './getRegistryAddressPrefill';
const idSuffix = nanoid();
export default function AddressFormSection({
  addressType,
  isNewCustomer
}) {
  const {
    atLeastOneChar,
    norwegianPostalCode,
    norwegianPostalCity
  } = useGetValidationString();
  const newCustomerFormContext = useFormContext();
  const addressFormContext = useFormContext();
  const formContext = isNewCustomer ? newCustomerFormContext : addressFormContext;
  const {
    register,
    errors,
    control,
    watch,
    trigger,
    setValue
  } = formContext;
  const addressErrors = (addressType ? errors[addressType] : errors) || {};
  const addressTypePrefix = addressType ? `${addressType}.` : '';
  const {
    t
  } = useTranslation();
  const {
    postal,
    addressField,
    field,
    postalCode
  } = useStyles();
  const [showMissingRegistryAddressInfo, setShowMissingRegistryAddressInfo] = useState(false);
  const currentCountry = watch(`${addressTypePrefix}countryCode`, 'NO');
  const currentlyNorway = currentCountry === DEFAULT_LANGUAGE;
  const currentlyNorwayRef = useRef(currentlyNorway);
  useEffect(() => {
    if (currentlyNorwayRef.current !== currentlyNorway) {
      currentlyNorwayRef.current = currentlyNorway;
      trigger([`${addressTypePrefix}postalCode`]);
    }
  }, [addressTypePrefix, currentlyNorway, trigger]);
  const {
    map: postalCodesMap
  } = usePostalCodes();
  const postalCodeValue = watch(`${addressTypePrefix}postalCode`);
  useEffect(() => {
    if (currentlyNorway) {
      const postalDistrict = postalCodesMap.get(postalCodeValue || '');
      setValue(`${addressTypePrefix}postalDistrict`, postalDistrict);
      trigger(`${addressTypePrefix}postalDistrict`);
    }
  }, [postalCodeValue, postalCodesMap, setValue, addressTypePrefix, currentlyNorway, trigger]);
  const postalDistrictError = currentlyNorway ? !postalCodesMap.has(postalCodeValue || '') && postalCodeValue && !addressErrors.postalCode : !!addressErrors.postalDistrict;
  const fetchAndSetPersonAddress = async () => {
    const userData = getUserDataFromSession();
    const registryAddressPrefill = getRegistryAddressPrefill(userData, isNewCustomer, addressType);
    setShowMissingRegistryAddressInfo(registryAddressPrefill.shouldShowMissingRegistryAddressInfo);
    if (!registryAddressPrefill.shouldPrefillFromRegistry) return;
    if (registryAddressPrefill.shouldShowMissingRegistryAddressInfo) {
      return;
    }
    try {
      setValue(`${addressTypePrefix}addressLine1`, registryAddressPrefill.addressLine1, {
        shouldValidate: true,
        shouldDirty: true
      });
      setValue(`${addressTypePrefix}postalCode`, registryAddressPrefill.postalCode, {
        shouldValidate: true,
        shouldDirty: true
      });
      setValue(`${addressTypePrefix}postalDistrict`, registryAddressPrefill.postalDistrict, {
        shouldValidate: true,
        shouldDirty: true
      });
    } catch (error) {
      console.error('Failed to fetch person data:', error);
    }
  };
  useEffect(() => {
    fetchAndSetPersonAddress();
  }, [addressType, isNewCustomer]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, showMissingRegistryAddressInfo && /*#__PURE__*/React.createElement("div", {
    className: field
  }, /*#__PURE__*/React.createElement(InfoBox, {
    warning: true
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(WarningRounded, {
    style: {
      marginRight: 8
    }
  }), t('address.missing_registry_address')))), /*#__PURE__*/React.createElement("div", {
    className: field
  }, /*#__PURE__*/React.createElement(FormElement, {
    title: t('address.first_address'),
    childId: `${addressTypePrefix}addressLine1_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    name: `${addressTypePrefix}addressLine1`,
    className: addressField,
    inputRef: register({
      required: true,
      minLength: 1
    }),
    margin: "normal",
    variant: "outlined",
    fullWidth: true,
    placeholder: t('address.first_address'),
    error: !!addressErrors.addressLine1,
    helperText: addressErrors.addressLine1 ? atLeastOneChar : '',
    inputProps: {
      id: `${addressTypePrefix}addressLine1_${idSuffix}`
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: field
  }, /*#__PURE__*/React.createElement(FormElement, {
    title: t('address.second_address'),
    childId: `${addressTypePrefix}addressLine2_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    name: `${addressTypePrefix}addressLine2`,
    className: addressField,
    inputRef: register,
    margin: "normal",
    variant: "outlined",
    fullWidth: true,
    placeholder: t('address.second_address'),
    InputProps: {
      endAdornment: /*#__PURE__*/React.createElement(InputAdornment, null)
    }
    // eslint-disable-next-line react/jsx-no-duplicate-props
    ,
    inputProps: {
      id: `${addressTypePrefix}addressLine2_${idSuffix}`
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: field
  }, /*#__PURE__*/React.createElement(FormElement, {
    title: t('address.care_of_name'),
    childId: `${addressTypePrefix}careOfName_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    name: `${addressTypePrefix}careOfName`,
    className: addressField,
    inputRef: register,
    margin: "normal",
    variant: "outlined",
    placeholder: t('address.care_of_name'),
    InputProps: {
      endAdornment: /*#__PURE__*/React.createElement(InputAdornment, null)
    },
    fullWidth: true
    // eslint-disable-next-line react/jsx-no-duplicate-props
    ,
    inputProps: {
      id: `${addressTypePrefix}careOfName_${idSuffix}`
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: field
  }, /*#__PURE__*/React.createElement("div", {
    className: postal
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormElement, {
    title: t('address.postal_code'),
    childId: `${addressTypePrefix}postalCode_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    name: `${addressTypePrefix}postalCode`,
    className: postalCode,
    inputRef: register({
      required: true,
      pattern: currentlyNorway ? postalCodeRegex : undefined,
      minLength: 1
    }),
    margin: "normal",
    variant: "outlined",
    placeholder: t('address.postal_code'),
    error: !!addressErrors.postalCode,
    helperText: addressErrors?.postalCode ? currentlyNorway ? norwegianPostalCode : atLeastOneChar : '',
    inputProps: {
      id: `${addressTypePrefix}postalCode_${idSuffix}`
    }
  }))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormElement, {
    title: t('address.postal_city'),
    childId: `${addressTypePrefix}postalDistrict_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    name: `${addressTypePrefix}postalDistrict`,
    className: addressField,
    inputRef: register({
      required: true,
      minLength: 1
    }),
    margin: "normal",
    variant: "outlined",
    placeholder: t('address.postal_city'),
    error: postalDistrictError,
    helperText: postalDistrictError ? currentlyNorway ? norwegianPostalCity : atLeastOneChar : '',
    inputProps: {
      id: `${addressTypePrefix}postalDistrict_${idSuffix}`,
      readOnly: currentlyNorway
    },
    InputProps: {
      className: currentlyNorway ? 'Mui-disabled' : undefined
    }
  }))))), /*#__PURE__*/React.createElement("div", {
    className: field
  }, control && /*#__PURE__*/React.createElement(FormCountrySelector, {
    control: control,
    name: `${addressTypePrefix}countryCode`,
    label: t('address.country')
  })));
}
AddressFormSection.defaultProps = {
  addressType: undefined
};